import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "arteconciencia_admin_token_v1";

const DAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MONTHS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(y, m - 1, d);
  return `${DAYS[dt.getDay()]} ${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}`;
}

/* Hoja imprimible del día para el equipo del Palacio Nacional.
   Pensada para papel: blanco/negro, tablas simples, botones ocultos al imprimir. */
export default function AdminDiaPrint() {
  const [params] = useSearchParams();
  const date = params.get("date") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useSEO({ title: "Hoja del día | Admin Arteconciencia", noindex: true });

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || !date) {
      setError("Falta la fecha o la sesión de administrador.");
      return;
    }
    fetch(`${API}/api/visits/admin/day-sheet?date=${date}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setData)
      .catch(() => setError("No se pudo cargar la hoja del día."));
  }, [date]);

  return (
    <div className="min-h-screen bg-white text-black">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 14mm; }
          body { -webkit-print-color-adjust: exact; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-paper border-b border-line px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
        <Link
          to="/admin/visitas"
          className="inline-flex items-center gap-2 font-ui uppercase tracking-[0.18em] text-[0.7rem] text-ink-soft hover:text-ink transition-colors"
          data-testid="print-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Volver
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink text-paper-raised hover:bg-latir-red font-ui uppercase tracking-[0.18em] text-[0.7rem] transition-colors"
          data-testid="print-button"
        >
          <Printer className="w-3.5 h-3.5" /> Imprimir hoja
        </button>
      </div>

      <div className="max-w-[820px] mx-auto px-6 sm:px-10 py-10 print:px-0 print:py-0" data-testid="day-sheet">
        {error && <p className="font-editorial italic text-latir-red">{error}</p>}
        {!data && !error && (
          <p className="flex items-center gap-3 font-ui text-[0.85rem] text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </p>
        )}
        {data && (
          <>
            <header className="border-b-2 border-black pb-5">
              <p className="font-ui uppercase tracking-[0.3em] text-[0.65rem] text-neutral-500">
                Arteconciencia · Palacio Nacional de El Salvador
              </p>
              <h1
                className="mt-2 font-display text-[2rem] leading-none"
                style={{ fontFamily: '"Instrument Serif", serif' }}
              >
                LATIR — Visitas guiadas
              </h1>
              <p className="mt-2 font-display text-[1.25rem] capitalize" style={{ fontFamily: '"Instrument Serif", serif' }} data-testid="day-sheet-date">
                {fmtDate(data.date)}
              </p>
            </header>

            {data.blocks.every((b) => b.bookings.length === 0) && (
              <p className="mt-8 font-editorial italic text-neutral-500">
                No hay reservas confirmadas para este día.
              </p>
            )}

            {data.blocks.map((b) => (
              <section key={b.time} className="mt-8" data-testid={`day-block-${b.time}`}>
                <div className="flex items-baseline justify-between gap-4 border-b border-black pb-2">
                  <h2 className="font-ui uppercase tracking-[0.16em] text-[0.8rem]">
                    {b.horario}
                  </h2>
                  <p className="font-ui text-[0.78rem] text-neutral-600">
                    {b.confirmed_count}/{b.capacity} personas
                  </p>
                </div>
                {b.bookings.length === 0 ? (
                  <p className="py-3 font-editorial italic text-[0.9rem] text-neutral-400">Sin reservas</p>
                ) : (
                  <table className="w-full mt-2 border-collapse">
                    <thead>
                      <tr className="text-left font-ui uppercase tracking-[0.14em] text-[0.62rem] text-neutral-500">
                        <th className="py-2 pr-3 font-normal w-8">#</th>
                        <th className="py-2 pr-3 font-normal">Responsable</th>
                        <th className="py-2 pr-3 font-normal w-16 text-right">Pers.</th>
                        <th className="py-2 pr-3 font-normal">Teléfono</th>
                        <th className="py-2 font-normal hidden sm:table-cell">Correo / comentarios</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.bookings.map((r, i) => (
                        <tr key={i} className="border-t border-neutral-200 align-top">
                          <td className="py-2.5 pr-3 text-[0.8rem] text-neutral-400">{String(i + 1).padStart(2, "0")}</td>
                          <td className="py-2.5 pr-3 text-[0.92rem]">{r.name}</td>
                          <td className="py-2.5 pr-3 text-[0.92rem] text-right">{r.party_size}</td>
                          <td className="py-2.5 pr-3 text-[0.85rem]">{r.phone}</td>
                          <td className="py-2.5 text-[0.8rem] text-neutral-500 hidden sm:table-cell">
                            {r.email}{r.notes ? ` · ${r.notes}` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            ))}

            <footer className="mt-10 border-t-2 border-black pt-4 flex items-baseline justify-between gap-4">
              <p className="font-ui uppercase tracking-[0.18em] text-[0.68rem] text-neutral-500">
                Totales del día
              </p>
              <p className="font-display text-[1.1rem]" style={{ fontFamily: '"Instrument Serif", serif' }} data-testid="day-sheet-totals">
                {data.total_bookings} reservas · {data.total_people} asistentes
              </p>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
